import { useEffect, useState } from "react";
import { ContentType } from "@financial-advisory/data-access/api-types";
import { TransparentButton } from "@financial-advisory/ui/components/buttons";
import { IconChevronRight, IconX } from "@tabler/icons-react";
import { Heading } from "@unlikelyai-magic/ui/typography";
import { cn } from "@unlikelyai-magic/utils";
import { ContentTypeColors } from "./PdfViewer/TextHighlight";

export const ContentTypeExplanation: Record<ContentType, string> = {
  RULE: "Requirements are descriptions of a person or company’s obligations in a certain situation. We extract these and then analyse them to determine how the application is required to behave at runtime. Extracting a requirement creates two decision trees: a ‘trigger’ and an ‘obligation’. The trigger describes who the requirement applies to (eg, “A firm designing targeted support frameworks”). The obligation describes what the person or company has to do (“the firm must have the consumer’s best interest at heart”).",
  GUIDANCE:
    "In some regulatory documents, the FCA provides generally phrased instructions about what ‘may’ or ‘may not’ be included in a certain definition or requirement. This content is useful for grounding the interpretation of the material and for resolving edge cases in the evaluation. This form of instruction is detected by the ingestion pipeline, categorised and stored alongside the definitions of the relevant topics. It is used to increase accuracy when the definition of a concept is evaluated.",
  DEFINITION:
    "Definitions are detailed descriptions of what is included in certain key regulatory topics. For example, the FCA defines in a lot of detail what counts as advice and what is merely information. These paragraphs are analysed and extracted into a decision tree. This tree can be referenced by other definitions; for example, the definition of a personalised recommendation references the definition of advice. They can also be referenced by requirements; for example, there is a requirement that only an appropriately authorised person can give advice.",
  EXAMPLE:
    "In some regulatory documents, the FCA provides explicit examples of what they do or don’t consider to be included in a definition. For example, their definition of advice is followed by several examples of advice and several examples of information. These examples are detected by the ingestion pipeline, categorised and stored alongside the definitions of the relevant topics. They are used to increase accuracy when the definition of a concept is evaluated.",
  CONTEXT:
    "Particularly in the opening material of a chapter or section, regulatory documents often provide contextual statements establishing the purpose or relevance of the following material. This content is useful for interpreting the rules which follow in the correct way. This context is detected by the ingestion pipeline, categorised and stored alongside the rules in the section which follow. It is used to increase accuracy when the rules are evaluated to determine whether they imply a runtime obligation for our use case.",
  NOT_APPLICABLE:
    "This section was determined to be not applicable to the application.",
};

/** This map is used for the labels of the highlight keys */
export const ContentTypeToHighlightKeyMap: Record<ContentType, string> = {
  RULE: "REQUIREMENT",
  GUIDANCE: "INSTRUCTION",
  DEFINITION: "DEFINITION",
  EXAMPLE: "EXAMPLE",
  CONTEXT: "CONTEXT",
  NOT_APPLICABLE: "NOT_APPLICABLE",
};

export const HighlightColorKeys = () => {
  const [showHighlightColorKeys, setShowHighlightColorKeys] =
    useState<boolean>(false);

  useEffect(() => {
    const handler = () => setShowHighlightColorKeys(true);
    document.addEventListener("openHighlightKeys", handler as EventListener);
    return () => {
      document.removeEventListener(
        "openHighlightKeys",
        handler as EventListener
      );
    };
  }, []);

  return (
    <div
      id="highlight-keys"
      className={cn(
        "bg-white flex flex-col gap-2 overflow-y-auto",
        showHighlightColorKeys
          ? "absolute top-0 left-0 right-0 bottom-0 z-10"
          : ""
      )}
    >
      <div className="sticky top-0 bg-white flex flex-row items-center gap-2 border-b border-gray-200 px-3 min-h-[3.375rem]">
        <TransparentButton
          icon={showHighlightColorKeys ? <IconX /> : <IconChevronRight />}
          onClick={() => setShowHighlightColorKeys(!showHighlightColorKeys)}
        />
        <Heading level="h5">Keys</Heading>
        <div className="ml-3 flex flex-row gap-2">
          {Object.entries(ContentTypeColors).map(([key, color]) => (
            <div key={key} className={cn("w-6 h-6 rounded", color)} />
          ))}
        </div>
      </div>
      {showHighlightColorKeys && (
        <div className="flex flex-col gap-4 px-3 py-2 pb-10">
          {Object.entries(ContentTypeExplanation).map(([key, explanation]) => (
            <div key={key} className="flex flex-col gap-2">
              <div
                className={cn(
                  "px-2 py-1 w-fit rounded-xl rounded capitalize text-center font-semibold",
                  ContentTypeColors[key as ContentType]
                )}
              >
                {ContentTypeToHighlightKeyMap[key as ContentType].replace(
                  "_",
                  " "
                )}
              </div>
              <div>{explanation}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
